import { ErrorMessage } from "@hookform/error-message";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDebouncedCallback } from "use-debounce";
import { TransformSelectInput } from "../../utils/transforms/transformSelectInput.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorLabel from "./errorLabel.tsx";

type Entity = {
  id: string;
  name: string;
};

type Props = {
  name: string;
  label: string;
  transform: TransformSelectInput;
  queryFn: (nameFilter: string) => Promise<Entity[]>;
  onChange?: (value: Maybe<string>) => void;
  canCreate?: boolean;
};

export default function SearchableSelect({
  name,
  label,
  transform,
  queryFn,
  onChange,
  canCreate = true,
}: Props) {
  const debouncedQueryFn = useDebouncedCallback(
    (inputValue: string, callback) => {
      queryFn(inputValue)
        .then((results) =>
          callback(
            results?.map((entity) => ({
              label: entity.name,
              value: entity.id,
            })) ?? [],
          ),
        )
        .catch((error) => {
          console.error(error);
          callback([]);
        });
    },
    250,
  );

  return (
    <div className="sm:col-span-4">
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>
      <div className="mt-1 flex gap-2 border-0 bg-transparent py-1.5">
        <Controller
          name={name}
          render={({ field }) => {
            return canCreate ? (
              <AsyncCreatableSelect
                className="!text-gray-900 sm:text-sm sm:leading-6 flex-grow"
                classNames={{
                  control: (state) =>
                    state.isFocused
                      ? "!border-sky-600 !shadow-sm"
                      : "border-grey-300",
                  option: () => "!text-gray-900",
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#bae6fd",
                  },
                })}
                placeholder={"Search..."}
                noOptionsMessage={() => "Please provide a search term"}
                cacheOptions
                defaultOptions
                loadOptions={debouncedQueryFn}
                {...field}
                onChange={(e) => field.onChange(transform.output(e))}
                value={transform.input(field.value)}
              />
            ) : (
              <AsyncSelect
                className="!text-gray-900 sm:text-sm sm:leading-6 flex-grow"
                classNames={{
                  control: (state) =>
                    state.isFocused
                      ? "!border-sky-600 !shadow-sm"
                      : "border-grey-300",
                  option: () => "!text-gray-900",
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#bae6fd",
                  },
                })}
                placeholder={"Search..."}
                noOptionsMessage={() => "Please provide a search term"}
                cacheOptions
                defaultOptions
                loadOptions={debouncedQueryFn}
                {...field}
                onChange={(e) => {
                  const updatedValue = transform.output(e);
                  field.onChange(updatedValue);
                  onChange && onChange(updatedValue);
                }}
                value={transform.input(field.value)}
              />
            );
          }}
        />
        <div className="flex-none p-1.5">
          <div className="w-5" />
        </div>
      </div>
      <ErrorMessage
        name={name}
        render={({ message }) => <ErrorLabel message={message} />}
      />
    </div>
  );
}
